import { API_BASE_URL } from '../config';

const getAuthParams = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return { user_id: user.user_id || 0, token: user.token || '' };
  } catch {
    return { user_id: 0, token: '' };
  }
};

class StreamChat {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.streamEndpoint = '/ai_stream.php';
  }

  async streamChat({
    messages,
    model,
    onChunk,
    onComplete,
    onError,
    extraParams = {}
  }) {
    const controller = new AbortController();

    const requestBody = {
      messages,
      model: model || 'doubao-seed-2-0-lite-260215',
      stream: true,
      temperature: extraParams.temperature || 0.7,
      max_tokens: extraParams.max_tokens || 4096,
      ...getAuthParams()
    };

    let fullText = '';
    let usage = null;
    let completed = false;
    let rawResponse = '';

    try {
      const fetchUrl = `${this.baseURL}${this.streamEndpoint}`;

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      const contentType = response.headers.get('Content-Type') || '';

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        console.error('[StreamChat] HTTP错误:', response.status, '响应内容:', errorText.substring(0, 500));
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (contentType.includes('application/json') && !contentType.includes('text/event-stream')) {
        try {
          const text = await response.text();
          const trimmed = text.trim();

          const isSSE = trimmed.startsWith('data:') || trimmed.includes('\ndata:');

          if (isSSE) {
            const lines = trimmed.split('\n');
            for (const line of lines) {
              const tl = line.trim();
              if (!tl || tl === 'data: [DONE]') continue;
              if (tl.startsWith('data: ')) {
                try {
                  const data = JSON.parse(tl.slice(6));
                  if (data.error) {
                    const errMsg = data.error.message || 'API 错误';
                    const errCode = data.error.code || 0;
                    console.error('[StreamChat] API返回错误:', errMsg);
                    if (errCode === 4011) {
                      localStorage.removeItem('user');
                      window.dispatchEvent(new CustomEvent('kicked-out', { detail: errMsg }));
                    }
                    completed = true;
                    onError?.(new Error(errMsg));
                    return controller;
                  }
                  if (data.choices?.[0]?.delta) {
                    const d = data.choices[0].delta;
                    const c = d.content || '';
                    const r = d.reasoning_content || '';
                    const t = c || r;
                    if (t) {
                      fullText += t;
                      onChunk?.(t, fullText);
                    }
                  }
                  if (data.usage) usage = data.usage;
                } catch {}
              }
            }
            completed = true;
            onComplete?.(fullText, usage);
          } else {
            const jsonData = JSON.parse(text);
            if (jsonData.error) {
              const errMsg = jsonData.error.message || 'API 错误';
              onError?.(new Error(errMsg));
            } else if (jsonData.choices && jsonData.choices[0]?.message?.content) {
              fullText = jsonData.choices[0].message.content;
              onChunk?.(fullText, fullText);
              if (jsonData.usage) usage = jsonData.usage;
              completed = true;
              onComplete?.(fullText, usage);
            } else {
              onError?.(new Error('AI返回了无法识别的JSON格式'));
            }
          }
        } catch (jsonErr) {
          console.error('[StreamChat] 解析失败:', jsonErr.message);
          onError?.(new Error('AI响应格式异常'));
        }
        return controller;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        const chunkText = decoder.decode(value, { stream: true });
        rawResponse += chunkText;

        buffer += chunkText;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || trimmedLine === 'data: [DONE]') {
            if (trimmedLine === 'data: [DONE]' && !completed) {
              completed = true;
              onComplete?.(fullText, usage);
            }
            continue;
          }

          if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.slice(6);
              const data = JSON.parse(jsonStr);

              if (data.error) {
                const errMsg = data.error.message || 'API 错误';
                const errType = data.error.type || '';
                console.error('[StreamChat] API返回错误:', errMsg, 'type:', errType);
                if (!completed) {
                  completed = true;
                  const err = new Error(errMsg);
                  err.isRateLimit = errMsg.includes('429') || errMsg.includes('Too Many') || errMsg.includes('rate') || errType === 'rate_limit_error';
                  err.isAuthError = errMsg.includes('401') || errMsg.includes('认证') || errMsg.includes('登录');
                  onError?.(err);
                }
                return controller;
              }

              if (data.choices && data.choices[0]?.delta) {
                const delta = data.choices[0].delta;
                const content = delta.content || '';
                const reasoning = delta.reasoning_content || '';
                const text = content || reasoning;
                if (text) {
                  fullText += text;
                  onChunk?.(text, fullText);
                }
              }

              if (data.usage) {
                usage = data.usage;
              }
            } catch (e) {
            }
          } else if (trimmedLine.startsWith('{') && !completed) {
            try {
              const data = JSON.parse(trimmedLine);
              if (data.error) {
                console.error('[StreamChat] 非SSE格式JSON错误:', data.error.message);
                completed = true;
                onError?.(new Error(data.error.message || 'API错误'));
                return controller;
              }
            } catch {}
          }
        }
      }

      if (!completed) {
        if (!fullText && rawResponse) {
          console.error('[StreamChat] 流结束但fullText为空! 原始响应前500字符:', rawResponse.substring(0, 500));
        }
        onComplete?.(fullText, usage);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[StreamChat] 异常:', error.message);
        if (fullText && !completed) {
          completed = true;
          onComplete?.(fullText, usage);
        } else {
          onError?.(error);
        }
      }
    }

    return controller;
  }

  async chat({
    messages,
    model,
    extraParams = {}
  }) {
    const usedModel = model || 'doubao-seed-2-0-lite-260215';

    const requestBody = {
      messages,
      model: usedModel,
      stream: false,
      ...extraParams,
      ...getAuthParams()
    };

    const response = await fetch(`${this.baseURL}/ai_stream.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    const data = await response.json();

    return {
      text: data.choices[0]?.message?.content || '',
      usage: data.usage
    };
  }

  async quickStream(prompt, options = {}) {
    const messages = Array.isArray(prompt) ? prompt : [
      { role: 'user', content: prompt }
    ];

    return new Promise((resolve, reject) => {
      this.streamChat({
        messages,
        ...options,
        onComplete: (text) => resolve(text),
        onError: reject
      });
    });
  }
}

const streamChatInstance = new StreamChat();
export default streamChatInstance;
